import { zodResolver } from '@hookform/resolvers/zod';

import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { Controller,useForm } from 'react-hook-form';

import { markReceivedAtFulfillmentCenterFormSchema } from '@ekhodealer/ekho-common';
import { Button, Checkbox, Group, SkeletonRect,Stack, Text, toast } from '@ekhodealer/ekho-common/rev';

import type { ActionItem } from '@ekhodealer/ekho-common';

import { formatTimestampToDate } from '@common/lib/time';

import { useGetMarkReceivedAtFulfillmentCenter } from '../../../api/progress/useGetMarkReceivedAtFulfillmentCenter';
import { useMarkReceivedAtFulfillmentCenter } from '../../../api/progress/useMarkReceivedAtFulfillmentCenter';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

interface MarkReceivedAtFulfillmentCenterDetailProps {
	actionItem: ActionItem;
}

interface FormData {
	isConfirmed: boolean;
}

/**
 * MarkReceivedAtFulfillmentCenterDetail component for marking a vehicle as received at fulfillment center.
 * Displays a checkbox for confirmation and a submit button.
 */
export const MarkReceivedAtFulfillmentCenterDetail = ({
	actionItem,
}: MarkReceivedAtFulfillmentCenterDetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);

	const markReceivedAtFulfillmentCenterMutation = useMarkReceivedAtFulfillmentCenter(orderId);
	const { data: existingData, isLoading: isLoadingData } =
		useGetMarkReceivedAtFulfillmentCenter(orderId);

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
	} = useForm<FormData>({
		resolver: zodResolver(markReceivedAtFulfillmentCenterFormSchema),
		defaultValues: {
			isConfirmed: false,
		},
	});

	// Pre-populate form with existing data
	useEffect(() => {
		if (existingData?.dateReceivedAtFulfillmentCenter) {
			reset({ isConfirmed: true });
		}
	}, [existingData, reset]);

	const submitHandler = handleSubmit(async (data: FormData) => {
		if (!data.isConfirmed) {
			return;
		}

		try {
			await markReceivedAtFulfillmentCenterMutation.mutateAsync();
			toast({
				message: 'Vehicle marked as received at fulfillment center successfully',
				variant: 'success',
			});
			// Navigate back to progress view on success
			navigateBack();
			// Reset form on success
			reset();
		} catch (err) {
			const errorMessage =
				err instanceof Error
					? err.message
					: 'Failed to mark vehicle as received at fulfillment center';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		}
	});

	return (
		<Stack itemsSpacing='24'>
			<Stack itemsSpacing='8'>
				<Text size='body' weight='semibold'>
					{actionItem.config.title}
				</Text>
				<Text size='bodySmall' color='secondary'>
					{actionItem.config.instructions ||
						'Confirm that the vehicle has been received at the fulfillment center. The date will be recorded automatically when you submit.'}
				</Text>
			</Stack>

			<Stack itemsSpacing='16'>
				{/* Show completion status if already complete */}
				{isLoadingData ? (
					<SkeletonRect width='100%' height={48} />
				) : existingData?.dateReceivedAtFulfillmentCenter ? (
					<Stack itemsSpacing='4'>
						<Text size='body' color='success'>
							✓ Received at fulfillment center
						</Text>
						<Text size='bodySmall' color='secondary'>
							Received on{' '}
							{formatTimestampToDate(new Date(existingData.dateReceivedAtFulfillmentCenter).getTime()) ?? '—'}
						</Text>
					</Stack>
				) : null}

				{/* Show form if not complete */}
				{!existingData?.dateReceivedAtFulfillmentCenter && (
					<>
						<Controller
							name='isConfirmed'
							control={control}
							render={({ field: { onChange, value } }) => (
								<Stack itemsSpacing='4'>
									<Group itemsSpacing='8' itemsAlignY='center' itemsAlignX='start'>
										<Checkbox
											isSelected={value}
											onChange={onChange}
											isDisabled={
												isSubmitting ||
												markReceivedAtFulfillmentCenterMutation.isPending
											}
											ariaLabel='Confirm vehicle has been received at fulfillment center'
										/>
										<Text size='bodySmall'>Confirm</Text>
									</Group>
									{errors.isConfirmed && (
										<Text size='footnote' color='error'>
											{errors.isConfirmed.message}
										</Text>
									)}
								</Stack>
							)}
						/>

						<Button
							size='large'
							isFullWidth
							onPress={() => {
								submitHandler();
							}}
							isLoading={
								isSubmitting || markReceivedAtFulfillmentCenterMutation.isPending
							}
							isDisabled={isLoadingData}>
							{actionItem.config.buttonTitle || 'Mark as received at fulfillment center'}
						</Button>
					</>
				)}
			</Stack>
		</Stack>
	);
};
